import { PencilSimpleIcon, UserIcon } from '@phosphor-icons/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useGetTrainers } from '@/api/trainers'
import { Button } from '@/components/ui/button'
import { DetailsListItem } from '@/components/ui/details-list'
import { EmailLink } from '@/components/ui/email-link'
import { Flex } from '@/components/ui/flex'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { PhoneLink } from '@/components/ui/phone-link'
import { EMPTY_VALUE } from '@/constants/ui'
import { popupFactory } from '@/factories'
import { useDesignVersionPath } from '@/hooks'
import { formatDateOnly } from '@/utils/date'

import { AccountEmailsMenu } from '../../shared/account-emails-menu'
import { ActiveStatusBadge } from '../../shared/active-status-badge'
import { DetailsSection } from '../../shared/details-section'
import { CountedTitleSkeleton, DetailsModalSkeleton } from '../../shared/details-skeleton'
import { PersonProfile } from '../../shared/person-profile'
import { formatTrainerServiceFee, getTrainerGenderLabel, getTrainerServices } from '../staff.utils'
import { TrainerFormModal } from '../trainer-form-modal'

interface TrainerDetailsModalProps {
  onClose: VoidFunction
  trainerId: number
}

export const TrainerDetailsModal = ({ onClose, trainerId }: TrainerDetailsModalProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const navigate = useNavigate()
  const { data, isLoading } = useGetTrainers()

  const clientsPath = useDesignVersionPath('clients', {
    legacy: '/dashboard/clients/legacy',
    new: '/dashboard/clients',
  })

  const trainer = data?.find((item) => item.id === trainerId)
  const services = trainer ? getTrainerServices(trainer) : []
  const isRemoved = !isLoading && !!data && !trainer

  // Note: closes once the record is gone from the list
  useEffect(() => {
    if (isRemoved) onClose()
  }, [isRemoved, onClose])

  const handleEdit = () => {
    if (!trainer) return
    popupFactory.open(TrainerFormModal, { mode: 'edit', trainer }, { suspendCurrent: true })
  }

  const handleViewClients = () => {
    onClose()
    navigate(clientsPath)
  }

  const footer = (
    <>
      <Button use="secondary" onClick={onClose}>
        {t('common:actions.close')}
      </Button>
      <Button disabled={!trainer} onClick={handleEdit}>
        {t('common:actions.edit')}
        <Icon icon={PencilSimpleIcon} />
      </Button>
    </>
  )

  return (
    <Modal
      title={t('organisation:staff.detailsModal.title')}
      headerIcon={UserIcon}
      footer={footer}
      onClose={onClose}
    >
      {isLoading || !trainer ? (
        <DetailsModalSkeleton
          sections={[
            {
              labels: [
                t('organisation:staff.detailsModal.email'),
                t('organisation:staff.detailsModal.phone'),
                t('organisation:staff.detailsModal.gender'),
              ],
              title: t('organisation:staff.detailsModal.contact'),
            },
            {
              labels: [
                t('organisation:staff.detailsModal.street'),
                t('organisation:staff.detailsModal.apartment'),
                t('organisation:staff.detailsModal.zipCode'),
              ],
              title: t('organisation:staff.detailsModal.address'),
            },
            {
              badgeRow: 0,
              labels: [
                t('organisation:staff.detailsModal.status'),
                t('organisation:staff.detailsModal.createdAt'),
              ],
              title: t('organisation:staff.detailsModal.account'),
            },
            {
              rows: 1,
              title: (
                <CountedTitleSkeleton
                  format={(count) => t('organisation:staff.detailsModal.services', { count })}
                />
              ),
            },
            {
              labels: [t('organisation:staff.detailsModal.clients')],
              title: t('organisation:staff.detailsModal.clientsTitle'),
            },
          ]}
        />
      ) : (
        <Flex direction="column" gap={20}>
          <PersonProfile
            actions={<AccountEmailsMenu email={trainer.user.email} />}
            user={trainer.user}
          />

          <DetailsSection title={t('organisation:staff.detailsModal.contact')}>
            <DetailsListItem label={t('organisation:staff.detailsModal.email')}>
              <EmailLink email={trainer.user.email} />
            </DetailsListItem>
            <DetailsListItem label={t('organisation:staff.detailsModal.phone')}>
              <PhoneLink phone={trainer.phone} />
            </DetailsListItem>
            <DetailsListItem label={t('organisation:staff.detailsModal.gender')}>
              {getTrainerGenderLabel(t, trainer.gender) || EMPTY_VALUE}
            </DetailsListItem>
          </DetailsSection>

          <DetailsSection title={t('organisation:staff.detailsModal.address')}>
            <DetailsListItem label={t('organisation:staff.detailsModal.street')}>
              {trainer.street || EMPTY_VALUE}
            </DetailsListItem>
            <DetailsListItem label={t('organisation:staff.detailsModal.apartment')}>
              {trainer.apartment || EMPTY_VALUE}
            </DetailsListItem>
            <DetailsListItem label={t('organisation:staff.detailsModal.zipCode')}>
              {trainer.zipCode || EMPTY_VALUE}
            </DetailsListItem>
          </DetailsSection>

          <DetailsSection title={t('organisation:staff.detailsModal.account')}>
            <DetailsListItem label={t('organisation:staff.detailsModal.status')}>
              <ActiveStatusBadge isActive={trainer.active} />
            </DetailsListItem>
            <DetailsListItem label={t('organisation:staff.detailsModal.createdAt')}>
              {formatDateOnly(new Date(trainer.createdAt))}
            </DetailsListItem>
          </DetailsSection>

          <DetailsSection
            title={t('organisation:staff.detailsModal.services', {
              count: services.length,
            })}
          >
            {services.length ? (
              services.map((service) => (
                <DetailsListItem key={service.serviceId.name} label={service.serviceId.name}>
                  {formatTrainerServiceFee(service)}
                </DetailsListItem>
              ))
            ) : (
              <DetailsListItem label={t('organisation:staff.detailsModal.noServices')} />
            )}
          </DetailsSection>

          <DetailsSection title={t('organisation:staff.detailsModal.clientsTitle')}>
            <DetailsListItem label={t('organisation:staff.detailsModal.clients')}>
              {trainer.clients.length}
            </DetailsListItem>
          </DetailsSection>

          {trainer.clients.length > 0 && (
            <div>
              <Button size="small" use="secondary" onClick={handleViewClients}>
                {t('organisation:staff.detailsModal.viewClients')}
              </Button>
            </div>
          )}
        </Flex>
      )}
    </Modal>
  )
}
